import CardActionArea from '@mui/material/CardActionArea';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Image from 'next/image';
import useTranslations from '@/hooks/useTranslations';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import { WORKFLOW_STEP_OUTCOME_ID } from '@/constants/global';
import { Link as LinkType } from '@/blocks/components/types';
import OutcomeChips, { type OutcomeChip } from './OutcomeChips';
import { usePathname } from 'next/navigation';

interface props {
  title: string;
  alternativeTitle?: string;
  onClick?: () => void;
  link?: string | LinkType;
  cardLink: string | LinkType;
  viewItemLabel: string;
  disabled: boolean;
  loading: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  image: string;
  subtitle?: string;
  showDiscoverButton?: boolean;
  chips?: OutcomeChip[];
}

export default function CardWithImage({
  title,
  alternativeTitle,
  onClick,
  link,
  cardLink,
  viewItemLabel,
  disabled,
  loading,
  objectFit,
  image,
  subtitle,
  showDiscoverButton,
  chips,
}: props) {
  const { t, locale } = useTranslations();
  const imageAlt = t('cards.imageAlt', { name: title });
  const marketingWebsiteBase = process.env.NEXT_PUBLIC_LINK_MARKETING_WEBSITE;
  const linkToMarketingWebsite =
    typeof link === 'string' || !marketingWebsiteBase
      ? undefined
      : `${marketingWebsiteBase}${locale}${link ? link.href : '/products'}`;
  const pathName = usePathname();
  const isOutcome = pathName.includes(WORKFLOW_STEP_OUTCOME_ID);

  return (
    <CardActionArea
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
      {...(onClick ? { onClick } : {})}
      {...(!onClick
        ? {
            component: Link,
            href: linkToMarketingWebsite || cardLink,
            scroll: false,
            target: link && typeof link !== 'string' && link.newTab ? '_blank' : '_self',
          }
        : {})}
      aria-label={viewItemLabel}
      disabled={disabled || loading}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          aspectRatio: isOutcome ? '4 / 3' : 'auto',
          '& img': {
            objectFit,
          },
        }}
      >
        {image && <Image src={image} alt={imageAlt} fill sizes='600px' />}
      </Box>

      <Stack
        direction='row'
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 0.75,
          py: 0.5,
        }}
      >
        <Stack direction='column' spacing={0}>
          {!!chips?.length && <OutcomeChips chips={chips} />}
          <Typography
            variant='body1'
            sx={{
              fontWeight: 'bold',
            }}
          >
            {alternativeTitle ?? title}
          </Typography>
          {!!subtitle && <Typography variant='body2'>{subtitle}</Typography>}
        </Stack>

        {showDiscoverButton && (
          <Button
            variant='text'
            color='secondary'
            size='small'
            endIcon={<ArrowForwardIcon />}
            disabled
            sx={{
              '&:disabled': {
                color: 'secondary.main',
              },
            }}
          >
            {typeof link == 'string'
              ? t('buttons.discover')
              : link
                ? link.text
                : t('buttons.discover')}
          </Button>
        )}
      </Stack>
    </CardActionArea>
  );
}
